import { ERROR_CODE } from '@/constants';
import { useSponsorCode } from '@/hooks/use-sponsor-code';
import {
    AccountAccount,
    AccountConsent,
    CircleCircle,
    CirclePartner,
    CircleValidateAndAssignSponsorshipRequest,
    CircleValidationFieldWithValue,
} from '@calm/evexia-web-sdk';
import { useAccessCode } from '@rhg/hooks/use-access-code';
import useAccount, { useLinkSamlAccounts } from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useChangeSponsorship } from '@rhg/hooks/use-change-sponsorship';
import { useGetCircle } from '@rhg/hooks/use-circle';
import useCircles from '@rhg/hooks/use-circles';
import { getConsentContext } from '@rhg/hooks/use-consent-context';
import { useCopyCircle } from '@rhg/hooks/use-copy-circle';
import { useDependentPartnerById } from '@rhg/hooks/use-dependent-partner';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useInvitationByCode } from '@rhg/hooks/use-invitation';
import { useInviteCode } from '@rhg/hooks/use-invite-code';
import useJoinSponsorship from '@rhg/hooks/use-join-sponsorship';
import { useGetMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { useCheckPartner } from '@rhg/hooks/use-partner';
import { useForceRefreshCircleRecommendations } from '@rhg/hooks/use-recommendations/use-force-refresh-circle-recommendations';
import {
    ConsentVersions,
    RegistrationFlow,
    SubmitFlowProps,
    SubmitFlowResult,
} from '@rhg/hooks/use-registration-context/types';
import { useGetUnsponsoredConfig } from '@rhg/hooks/use-unsponsored-config';
import { useUpdateAccountConsent } from '@rhg/hooks/use-update-consent';
import { useUpgradeSponsorship } from '@rhg/hooks/use-upgrade-sponsorship';
import { logger } from '@rhg/libs/logger';
import { objectEntries } from '@rhg/utils/arrays';
import { identifyApiErrorCode } from '@rhg/utils/axios';
import { DEFAULT_COUNTRY_CODE } from '@rhg/utils/constants';
import {
    createInvitationError,
    getInvitationStatus,
    getInviteValidationFields,
    InvitationStatus,
} from '@rhg/utils/invitations';
import { getPartnerCircleSponsorshipUpsertRequest } from '@rhg/utils/partner';
import {
    clearStoredPartnerActivation,
    getActivationTokenHeader,
    isActivationTokenExpired,
} from '@rhg/utils/partner-activation';
import sleep from '@rhg/utils/sleep';
import {
    clearSAMLStorage,
    getPartnerPortalTokenData,
    getSSOSessionStorageData,
} from '@rhg/utils/sso';
import { useMutation } from '@tanstack/react-query';
import { useNeedsRegistrationConsent } from '../use-needs-registration-consent';

export const SSO_LINKING_ERROR = 'Missing SSO linking data';
export const MISSING_HIPAA_ERROR = 'Missing HIPAA signature';
export const JOIN_SPONSORSHIP_ERROR = 'Join Sponsorship Failed';
export const PARTNER_ACTIVATION_ERROR = 'Partner Activation Token Invalid';
export const PARTNER_ACTIVATION_EXPIRED_ERROR =
    'Partner Activation Token Expired';
export const NO_CIRCLE_ERROR = 'No Circle to submit';

function throwActivationTokenError(
    error: unknown,
    activationToken: string
): never {
    const errorCode = identifyApiErrorCode(error);
    if (errorCode === ERROR_CODE.ACTIVATION_TOKEN_PARTNER_MISMATCH) {
        clearStoredPartnerActivation();
        throw new Error(PARTNER_ACTIVATION_ERROR);
    }
    if (errorCode !== ERROR_CODE.INVALID_ACTIVATION_TOKEN) {
        throw new Error(JOIN_SPONSORSHIP_ERROR);
    }
    clearStoredPartnerActivation();
    const header = getActivationTokenHeader(activationToken);
    if (header && isActivationTokenExpired(header.expiresAt)) {
        throw new Error(PARTNER_ACTIVATION_EXPIRED_ERROR);
    }
    throw new Error(PARTNER_ACTIVATION_ERROR);
}

function getSSOValidationFields(samlToken: string, partner?: CirclePartner) {
    return getPartnerCircleSponsorshipUpsertRequest(
        partner,
        getPartnerPortalTokenData(samlToken || '')
    ).validationFields;
}

export function getSponsorshipRequest({
    partner,
    consents,
    activationToken,
    isSSO,
    willUseSSOTokenEligibility,
    ssoToken,
    eligibilityFormValues,
}: {
    partner?: CirclePartner | null;
    consents: CircleValidateAndAssignSponsorshipRequest['consents'];
    activationToken?: string;
    isSSO?: boolean;
    willUseSSOTokenEligibility?: boolean;
    ssoToken?: string | null;
    eligibilityFormValues?: CircleValidationFieldWithValue[];
}): CircleValidateAndAssignSponsorshipRequest {
    const validationFields = activationToken
        ? []
        : isSSO && willUseSSOTokenEligibility
        ? getSSOValidationFields(ssoToken ?? '', partner ?? undefined)
        : eligibilityFormValues;
    return {
        parentPartnerId: partner?.parentPartner?.id,
        validationFields,
        consents,
    };
}

export function useSubmitFlow({
    account,
    accountConsents,
}: {
    account?: AccountAccount;
    accountConsents: AccountConsent[];
}) {
    const { refetch: refetchAccount } = useAccount();
    const { activeCircle, setActiveCircle } = useActiveCircle();
    const { joinSponsorshipAsync } = useJoinSponsorship();
    const { refetch: refetchCircles } = useCircles();
    const { getMostRecentSponsorshipAsync } = useGetMostRecentSponsorship();
    const { getUnsponsoredConfigAsync } = useGetUnsponsoredConfig();
    const { changeSponsorshipAsync } = useChangeSponsorship();
    const { forceRefreshRecommendationsAsync } =
        useForceRefreshCircleRecommendations();
    const { accessCode: _accessCode, setAccessCode } = useAccessCode();
    const { removeSponsorCode } = useSponsorCode();
    const { inviteCode } = useInviteCode();
    const { invitation } = useInvitationByCode(inviteCode);
    const { copyCircleAsync } = useCopyCircle();
    const { checkPartnerAsync } = useCheckPartner();
    const { upgradeSponsorshipAsync } = useUpgradeSponsorship();
    const { dependentPartner: invitePartner, refetch: refetchInvitePartner } =
        useDependentPartnerById({
            dependentPartnerId: invitation?.attributes?.partnerId,
            countryCode: account?.countryCode ?? DEFAULT_COUNTRY_CODE,
        });
    const { updateConsentAsync } = useUpdateAccountConsent();
    const { linkSamlAccountsAsync: linkAccounts } = useLinkSamlAccounts();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();
    const { getCircleAsync: fetchCircle } = useGetCircle();
    const { ssoToken, isLinkedSsoUser } = getSSOSessionStorageData();
    const { setNeedsRegistrationConsent } = useNeedsRegistrationConsent();

    // The post-join recommendations refresh is a best-effort care-plan pre-warm:
    // its result is never consumed in this flow and the data is re-fetched on the
    // home screen. A transient recommendations 5xx must not abort registration
    // (which would otherwise surface the generic error dialog and block the
    // consent step), so its failure is logged and swallowed rather than rejecting
    // the submit flow.
    const preWarmRecommendations = async (circle: CircleCircle) => {
        try {
            await forceRefreshRecommendationsAsync({ circle });
        } catch (error) {
            logger.error(
                'Recommendations pre-warm failed during registration; continuing',
                {},
                error as Error
            );
        }
    };

    const { mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            eligibilityFormValues,
            registrationFlow,
            isSSO,
            willUseSSOTokenEligibility,
            isLinkingOnly,
            minConsentVersions,
            hipaaSignature,
            circle = activeCircle,
            accessCode = _accessCode,
            destinationPartner,
            activationToken,
        }: SubmitFlowProps): Promise<SubmitFlowResult> => {
            async function runMutation() {
                let submissionCircleId: number | undefined =
                    circle?.recipient.id;
                const consents = objectEntries<ConsentVersions>(
                    minConsentVersions || ({} as ConsentVersions)
                ).map((args) => {
                    const [key, value] = args!;
                    return {
                        consentType: key,
                        consentVer: value,
                    };
                });

                if (isSSO && isLinkingOnly) {
                    try {
                        await linkAccounts();
                    } catch {
                        throw new Error(SSO_LINKING_ERROR);
                    }
                } else if (
                    registrationFlow === RegistrationFlow.LossOfSponsorship
                ) {
                    if (!hipaaSignature) {
                        throw new Error(MISSING_HIPAA_ERROR);
                    }
                    if (minConsentVersions?.chdpp) {
                        await updateConsentAsync({
                            id: accountConsents[0].consentId ?? 0,
                            chdppVersion: minConsentVersions?.chdpp ?? 0,
                        });
                    }
                    if (!circle) {
                        throw new Error(NO_CIRCLE_ERROR);
                    }
                    const response = await copyCircleAsync({
                        healthcarePortabilityVersion:
                            minConsentVersions?.healthcare_portability ?? 0,
                        consentSig: hipaaSignature,
                        circle,
                    });

                    submissionCircleId = response?.newRecipientId;
                    const newCircle = await fetchCircle(submissionCircleId!);
                    void preWarmRecommendations(newCircle);
                    await refetchCircles();
                    setActiveCircle(newCircle);
                } else if (
                    registrationFlow === RegistrationFlow.SkipSponsorship
                ) {
                    await updateConsentAsync({
                        id: accountConsents[0].consentId ?? 0,
                        chdppVersion: accountConsents[0]?.minCHDPPVersion ?? 0,
                    });
                } else if (
                    registrationFlow === RegistrationFlow.ChangeOfSponsorship
                ) {
                    if (isSSO && !isLinkedSsoUser) {
                        if (!ssoToken) {
                            throw new Error(SSO_LINKING_ERROR);
                        }

                        try {
                            await linkAccounts();
                        } catch {
                            throw new Error(SSO_LINKING_ERROR);
                        }
                    }
                    try {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        const partner = await checkPartnerAsync({
                            circle,
                            code: accessCode,
                        });
                        const newCircle = await changeSponsorshipAsync({
                            partnerId: partner!.id,
                            circle,
                            willRefreshAllCircles: false,
                            activationToken,
                            request: getSponsorshipRequest({
                                partner,
                                consents,
                                activationToken,
                                isSSO,
                                willUseSSOTokenEligibility,
                                ssoToken,
                                eligibilityFormValues,
                            }),
                        });
                        submissionCircleId = newCircle.recipient.id;
                        void preWarmRecommendations(newCircle);
                        clearSAMLStorage();
                        await refetchCircles();
                        setActiveCircle(newCircle);
                    } catch (e) {
                        // Send users to error screen with support email on SSO sponsorship failure
                        if (activationToken) {
                            throwActivationTokenError(e, activationToken);
                        } else if (isSSO && willUseSSOTokenEligibility) {
                            throw new Error(JOIN_SPONSORSHIP_ERROR);
                        } else {
                            throw e;
                        }
                    }
                } else if (
                    registrationFlow === RegistrationFlow.UpgradeSponsorship
                ) {
                    // No SSO support: unlike ChangeOfSponsorship, this branch
                    // never checks isSSO/willUseSSOTokenEligibility, since
                    // Upgrade has no SSO entry path.
                    if (!circle) {
                        throw new Error(NO_CIRCLE_ERROR);
                    }
                    // Caller-supplied: the Home banner passes useBuyUpEligibility's
                    // value, access-code entry passes what it detected on the
                    // legacy code (EVE-14056) — never a fresh access-code lookup.
                    if (!destinationPartner) {
                        throw new Error(
                            'Destination partner is required for UpgradeSponsorship'
                        );
                    }
                    // Fetched by code (not getPartnerByIdAsync) so this
                    // shares its react-query cache entry with
                    // dialog/manager's usePartnerByCode, which resolves the
                    // same destination partner while the upgrade dialog is
                    // open.
                    const destinationPartnerConfig = await checkPartnerAsync({
                        circle,
                        code: destinationPartner.slug,
                    });
                    if (!destinationPartnerConfig) {
                        throw new Error('Destination partner config not found');
                    }
                    const newCircle = await upgradeSponsorshipAsync({
                        partnerId: destinationPartnerConfig.id,
                        circle,
                        willRefreshAllCircles: false,
                        request: {
                            parentPartnerId:
                                destinationPartnerConfig.parentPartner?.id,
                            validationFields: eligibilityFormValues,
                            consents,
                        },
                    });
                    submissionCircleId = newCircle.recipient.id;
                    void preWarmRecommendations(newCircle);
                    await refetchCircles();
                    setActiveCircle(newCircle);
                } else if (
                    registrationFlow ===
                    RegistrationFlow.ChangeOfInviteeSponsorship
                ) {
                    if (
                        invitation &&
                        getInvitationStatus(invitation) ===
                            InvitationStatus.Pending &&
                        invitePartner
                    ) {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        const newCircle = await changeSponsorshipAsync({
                            partnerId: invitePartner.id,
                            circle,
                            willRefreshAllCircles: false,
                            request: {
                                parentPartnerId:
                                    invitePartner?.parentPartner?.id,
                                validationFields:
                                    getInviteValidationFields(inviteCode),
                                consents,
                            },
                        });
                        submissionCircleId = newCircle.recipient.id;
                        void preWarmRecommendations(newCircle);
                        clearSAMLStorage();
                        await refetchCircles();
                        setActiveCircle(newCircle);
                    } else {
                        throw createInvitationError(
                            invitation &&
                                getInvitationStatus(invitation) ===
                                    InvitationStatus.Claimed
                                ? 'claimed'
                                : 'expired'
                        );
                    }
                } else if (
                    registrationFlow ===
                        RegistrationFlow.NewInviteeRegistration ||
                    registrationFlow ===
                        RegistrationFlow.ExistingInviteeRegistration
                ) {
                    if (
                        invitation &&
                        getInvitationStatus(invitation) ===
                            InvitationStatus.Pending &&
                        invitePartner
                    ) {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        // No SSO support needed
                        const newCircle = await joinSponsorshipAsync({
                            partnerId: invitePartner.id,
                            circle,
                            request: {
                                parentPartnerId:
                                    invitePartner?.parentPartner?.id,
                                validationFields:
                                    getInviteValidationFields(inviteCode),
                                consents,
                            },
                            willRefreshAllCircles: false,
                        });
                        submissionCircleId = newCircle.recipient.id;
                        void preWarmRecommendations(newCircle);
                        await refetchCircles();
                    } else {
                        throw createInvitationError(
                            invitation &&
                                getInvitationStatus(invitation) ===
                                    InvitationStatus.Claimed
                                ? 'claimed'
                                : 'expired'
                        );
                    }
                } else {
                    if (isSSO && !isLinkedSsoUser) {
                        if (!ssoToken) {
                            throw new Error(SSO_LINKING_ERROR);
                        }

                        try {
                            await linkAccounts();
                        } catch {
                            throw new Error(SSO_LINKING_ERROR);
                        }
                    }
                    try {
                        if (!circle) {
                            throw new Error(NO_CIRCLE_ERROR);
                        }
                        const partner = await checkPartnerAsync({
                            circle,
                            code: accessCode,
                        });
                        const newCircle = await joinSponsorshipAsync({
                            partnerId: partner!.id,
                            circle,
                            activationToken,
                            request: getSponsorshipRequest({
                                partner,
                                consents,
                                activationToken,
                                isSSO,
                                willUseSSOTokenEligibility,
                                ssoToken,
                                eligibilityFormValues,
                            }),
                            willRefreshAllCircles: false,
                        });
                        submissionCircleId = newCircle.recipient.id;
                        void preWarmRecommendations(newCircle);
                        await refetchCircles();
                    } catch (e) {
                        // Send users to error screen with support email on SSO sponsorship failure
                        if (activationToken) {
                            throwActivationTokenError(e, activationToken);
                        } else if (isSSO && willUseSSOTokenEligibility) {
                            throw new Error(JOIN_SPONSORSHIP_ERROR);
                        } else {
                            throw e;
                        }
                    }
                }

                // Need to flag that the user's consents should be shown in the registration flow, until registration is complete
                await setNeedsRegistrationConsent(true);

                const isLossOfSponsorship =
                    registrationFlow === RegistrationFlow.LossOfSponsorship;

                // No need to refresh if lost because we no longer have a sponsor or partner
                if (!isLossOfSponsorship) {
                    // Upgrade never resolves its partner via access code (it
                    // uses getPartnerByIdAsync), so there's no access-code-keyed
                    // partner cache entry to refresh for this flow.
                    const shouldCheckPartner =
                        accessCode &&
                        circle &&
                        registrationFlow !==
                            RegistrationFlow.UpgradeSponsorship;
                    await Promise.all([
                        ...(shouldCheckPartner
                            ? [
                                  checkPartnerAsync({
                                      circle,
                                      code: accessCode,
                                  }),
                              ]
                            : []),
                        ...(inviteCode ? [refetchInvitePartner()] : []),
                    ]);
                }

                // Build post-submission consent requirements
                const refreshedSubmissionCircle = submissionCircleId
                    ? await fetchCircle(submissionCircleId)
                    : undefined;
                const accountConsentData = (await refetchConsent()).data;
                const newSponsorship =
                    !isLossOfSponsorship && refreshedSubmissionCircle
                        ? await getMostRecentSponsorshipAsync(
                              refreshedSubmissionCircle
                          )
                        : await getUnsponsoredConfigAsync();
                const consentRequirements = getConsentContext({
                    accountConsents: accountConsentData
                        ? [accountConsentData.consentData]
                        : [],
                    accountUserConsents: accountConsentData
                        ? [accountConsentData?.userResponse]
                        : [],
                    sponsorConsents: newSponsorship?.consents,
                });

                return {
                    registrationFlow,
                    consentRequirements,
                    circle: refreshedSubmissionCircle,
                    account: (await refetchAccount()).data,
                };
            }

            const shouldHaveMinLoading =
                registrationFlow === RegistrationFlow.LossOfSponsorship ||
                registrationFlow === RegistrationFlow.ChangeOfSponsorship ||
                registrationFlow ===
                    RegistrationFlow.ChangeOfInviteeSponsorship ||
                registrationFlow === RegistrationFlow.UpgradeSponsorship;
            const [submission] = await Promise.all([
                runMutation(),
                shouldHaveMinLoading && sleep(3000),
            ]);

            return submission;
        },
        onSuccess: () => {
            // Don't want to keep the SAML/SSO linking tokens around after success
            clearSAMLStorage();
            // Reset access code state
            setAccessCode('');
            removeSponsorCode();
            clearStoredPartnerActivation();
        },
        onError: (_, { isSSO, willUseSSOTokenEligibility }) => {
            // Don't want to keep SSO/SAML linking tokens around after non-manual failure so that the user isn't forced to link SSO when it will not work
            if (!isSSO || willUseSSOTokenEligibility) clearSAMLStorage();
        },
    });

    return {
        submitFlow: mutateAsync,
        ...otherProps,
    };
}
